import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

import MDBox from "components/MDBox";
import { Success, Error } from "ui-components/Alert";
import { formRender, initialValues, kitSKUValidation } from "./kit-sku-schema";
import {
  formRender as detailRender,
  initialValues as detailValues,
  kitSKUDetailValidation,
} from "./kit-sku-detail-schema";
import KitSKUInfo from "./components/KitSKUInfo";
import { kitSKUColumns } from "./components/columns";
import { StripedDataGrid } from "layouts/views/components/Grid";
import { getSKUKit, editSKUKit } from "services/data.internal.service";
import styled from "@emotion/styled";
import { InboundSaveButton } from "ui-components/Form";
import { CancelButton } from "ui-components/IconButton";
import { Card } from "@mui/material";
import { InboundLayout } from "ui-components/Layout";
import KitSKUDetailInfo from "./components/KitSKUDetailInfo";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";

const defaultValues = {
  ...initialValues,
};
function EditKitSKU() {
  const location = useLocation();
  const { customerCode, skuCode } = location?.state ?? {};
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [formValues, setFormValues] = useState(defaultValues);

  const [detail, setDetail] = useState([]);
  const queryClient = useQueryClient();

  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  useQuery({
    queryKey: ["sku-kit", customerCode, skuCode],
    queryFn: () => getSKUKit({ customerCode, skuCode }),
    onSuccess: (data) => {
      const { details, ...values } = data.data.data;
      setFormValues(values);
      let counter = 1;
      const detailWithId = details.map(x => {
        return {
          id: counter,
          lineNo: counter++,
          ...x
        }
      })
      console.log(details, detailWithId)
      setDetail(detailWithId);
    },
    onError,
  })

  const editSKUKitMutation = useMutation(editSKUKit, {
    onSuccess: (data) => {
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });  

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(kitSKUValidation),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
    reset,
  } = form;

  const infoProps = {
    control,
    errors,
    formRender,
    mode: "EDIT",
  };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    if (detail.length <= 0){
      setAlert(<Error content="No details" onChange={setAlert}/>);
      return;
    }

    const { ...data } = getValues();

    const req = {
      ...data,
      details: detail.map((x) => {
        const { createdOn, ...data } = x;
        return {
          createdOn: createdOn ? dayjs(createdOn, "DD / MM / YYYY").format("YYYY-MM-DD") : null,
          ...data,
        };
      }),
    };
    console.log(req)

    editSKUKitMutation.mutate(
    {
      customerCode,
      skuCode,
      req
    }, {
      onSuccess: () => {
        reset();
        queryClient.invalidateQueries(["sku-kit"]);
      },
    });
  };
  const editStatus = editSKUKitMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {alert}
      {dialog}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <KitSKUInfo {...infoProps} />
          </form>
        </MDBox>
      </Card>
      <MDBox sx={{ margin: "1rem 0" }} />
      <KitSKUDetail customerCode={customerCode} detail={detail} setDetail={setDetail} />
      <InboundSaveButton
        label="submit"
        disabled={editStatus}
        url="/setup/sku"
        onClick={onSubmit}
      />
    </InboundLayout>
  );
}

const KitSKUDetail = ({ customerCode, detail, setDetail }) => {
  const [alert, setAlert] = useState(null);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        {alert}
        <DetailGrid
          customerCode={customerCode}
          detail={detail}
          setDetail={setDetail}
          setAlert={setAlert}
        />
      </MDBox>
    </Card>
  );
};

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
});

const DetailGrid = ({ customerCode, detail, setDetail, setAlert }) => {
  const handleDeleteClick = (req) => {
    let newDet = detail.filter((x) => x.id !== req.id);
    newDet = newDet.map((x) => {
      if (x.id > req.id) {
        x.id = x.id - 1;
        x.lineNo = x.lineNo - 1;
      }
      return x;
    });
    setDetail(newDet);
  };
  const columns = [
    ...kitSKUColumns,
    {
      field: "createdOn",
      headerName: "Created On",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <CancelButton
            title="Delete"
            onClick={() => {
              handleDeleteClick(row);
            }}
          />,
        ];
      },
    },
  ];

  const [formValues, setFormValues] = useState({ ...detailValues });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(kitSKUDetailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    trigger,
    getValues,
    setValue,
    reset,
  } = form;

  const detailProps = {
    control,
    errors,
    formRender: detailRender,
    watch,
    setValue,
    customerCode,
  };

  const onSubmit = async () => {
    //set Detail
    const isValid = await trigger();
    if (!isValid) return;

    const {skuCode, skuDescription, quantity} = getValues();
    if (detail.some(x => x.skuCode === skuCode)){
      setAlert(<Error content={"SKU Code existed"} onChange={setAlert} />);
      return;
    }

    let lineNo = (detail.length ?? 0) + 1;

    const newDetail = [...detail, {
      id: lineNo,
      lineNo: lineNo,
      skuCode,
      skuDescription,
      quantity,
    }];

    setDetail(newDetail);
    reset();
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <KitSKUDetailInfo {...detailProps} />
      </form>
      <EditableStyledDataGrid
        columns={columns}
        rows={detail}
        sx={{
          borderRadius: "0.75rem",
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        getRowHeight={() => "auto"}
        hideFooterSelectedRowCount={true}
      />
    </>
  );
};

export default EditKitSKU;
